import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * /learning hub (BW18 PR-J).
 *
 * Single sub-area at first: Vocabulary. Reading lists / books / podcasts
 * land in later build weeks; surface the daily queue size on the hub card.
 */
export function Learning() {
  const queue = useDueQueueSize();

  return (
    <article aria-labelledby="learning-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Learning
      </p>
      <h1
        id="learning-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        Daily reps. Slow compounding.
      </h1>

      <Link
        to="/learning/vocabulary"
        style={{
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <section
          style={{
            background: "var(--bg-2)",
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            padding: "var(--space-5)",
            display: "grid",
            gridTemplateColumns: "max-content 1fr max-content",
            gap: "var(--space-4)",
            alignItems: "center",
          }}
        >
          <BookOpen size={28} strokeWidth={1.5} color="var(--accent)" />
          <div>
            <h2 style={{ margin: 0, fontSize: "var(--type-body-l)", fontWeight: 500 }}>
              Vocabulary
            </h2>
            <p style={{ margin: "4px 0 0", color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
              {queue.isLoading
                ? "Loading queue…"
                : !queue.data
                  ? "Queue clear. New cards refresh tomorrow."
                  : queue.data === 1
                    ? "1 card due. About 1 minute."
                    : `${queue.data} cards due. About ${Math.max(1, Math.round(queue.data / 6))} min review.`}
            </p>
          </div>
          <ArrowRight size={18} strokeWidth={1.5} color="var(--fg-3)" />
        </section>
      </Link>
    </article>
  );
}

function useDueQueueSize() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["vocab_due_count", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return 0;
      const now = new Date().toISOString();
      const { count, error } = await supabase
        .from("vocab_translation")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .lte("due_at", now);
      if (error) throw error;
      return count ?? 0;
    },
  });
}
